import React, { useEffect, useState, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { formatDMY, PrimaryColor } from "../common";
import { enGB } from "date-fns/locale";
import ReactDOM from "react-dom";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
export const formatDateString = (date: Date | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return ""; // Return empty string or a default date format if date is invalid
    }
    return date.toLocaleDateString("en-GB").split("/").reverse().join("-");
  };
  
interface DynamicDatePickerProps {
  setDate: (dates: { startDate: Date; endDate: Date; key: string }[]) => void;
  defaultDates?: { startDate: Date; endDate: Date; key: string };
  minDate?: string;
  maxDate?: string;
  selectedDates?:
    | { startDate: Date; endDate: Date; key: string }
    | Array<{ value: string; startDate?: Date; endDate?: Date }>;
}

export const DynamicDatePicker: React.FC<DynamicDatePickerProps> = ({
  setDate,
  defaultDates,
  selectedDates = [],
  minDate = "2021-01-01",
  maxDate = "2026-12-31",
}) => {
  const [visible, setVisible] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  // Button reference to position the picker relative to button
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultDates) {
      setDateRange(defaultDates);
    }
  }, [defaultDates]);

  const handleDateChange = (item: any) => {
    setDateRange({
      startDate: item.selection.startDate || new Date(),
      endDate: item.selection.endDate || new Date(),
      key: item.selection.key || "selection",
    });
  };

  const handleSubmit = () => {
    // Only proceed if dateRange exists
    if (dateRange) {
      // Format the dates in a consistent format for comparison
      const formattedStartDate =
        dateRange.startDate.toLocaleDateString("en-GB");
      const formattedEndDate = dateRange.endDate.toLocaleDateString("en-GB");

      // Check if the date range matches the defaultDates (if provided)
      const matchesDefault = defaultDates
        ? defaultDates.startDate.toLocaleDateString("en-GB") ===
            formattedStartDate &&
          defaultDates.endDate.toLocaleDateString("en-GB") === formattedEndDate
        : false;

      // Only set the date if it's a valid range (different start and end dates)
      // and it doesn't match the already set default dates
      if (!matchesDefault) {
        // Convert dates to the required format and set them
        setDate([
          {
            startDate: new Date(
              formattedStartDate.split("/").reverse().join("-")
            ),
            endDate: new Date(formattedEndDate.split("/").reverse().join("-")),
            key: "selection",
          },
        ]);
      }
    }

    // Close the date picker regardless of whether we set a new date
    setVisible(false);
  };

  // Compute all disabled dates
  const disabledDates: Date[] = [];

  if (typeof selectedDates === "object" && !Array.isArray(selectedDates)) {
    let current = new Date(selectedDates.startDate);
    const end = new Date(selectedDates.endDate);
    while (current <= end) {
      disabledDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  } else if (Array.isArray(selectedDates)) {
    selectedDates.forEach((range) => {
      const date = String(range.value).slice(0, 21).split(",");
      let current = new Date(date[0]);
      const end = new Date(date[1]);
      while (current <= end) {
        disabledDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    });
  }

  // Close picker when clicking outside
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Skip if click was on the button itself
      if (buttonRef.current?.contains(e.target as Node)) return;

      const datePickerElements = document.querySelectorAll(
        ".date-selection-wrapper, .rdrDateRangePickerWrapper"
      );
      let clickedInside = false;

      datePickerElements.forEach((el) => {
        if (el.contains(e.target as Node)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible]);

  // Render the DatePicker at the top of the page using portal
  const renderDatePickerPortal = () => {
    if (!visible) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
        <div
          className="date-selection-wrapper  shadow-2xl rounded-md pointer-events-auto"
          style={{
            maxWidth: "96vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <div className="">
            <DateRangePicker
              onChange={handleDateChange}
              showPreview={true}
              dragSelectionEnabled={false}
              minDate={new Date(minDate)}
              maxDate={new Date(maxDate)}
              moveRangeOnFirstSelection={false}
              rangeColors={[PrimaryColor, PrimaryColor, PrimaryColor]}
              color={PrimaryColor}
              locale={enGB}
              months={2}
              ranges={
                dateRange
                  ? [dateRange]
                  : [
                      {
                        startDate: new Date(),
                        endDate: new Date(),
                        key: "selection",
                      },
                    ]
              }
              direction="horizontal"
              disabledDates={disabledDates}
            />
            <div className="flex justify-center mt-4">
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-[#F1935C] text-white rounded-lg"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setVisible(!visible)}
        className="date-range-button"
      >
        {defaultDates ? (
          <>
            <span className="date-range">
              {formatDMY(formatDateString(dateRange!.startDate))}
            </span>{" "}
            to{" "}
            <span className="date-range">
              {formatDMY(formatDateString(dateRange!.endDate))}
            </span>
          </>
        ) : (
          <span className="date-range">Add Date Range</span>
        )}
      </button>

      {renderDatePickerPortal()}
    </div>
  );
};
